import { useState, useCallback, useRef, useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import { PERSON_BANDS_QUERY } from "@/pages/people/[pgaId]/person/queries";
import { Band } from "@/types";

export interface BandChangeExpectation {
  sectionLegacyId?: string;
  classificationName?: string;
  startedOn?: string;
}

export interface BandChanges {
  detected: boolean;
  newBands: Band[];
  updatedBands: Band[];
  terminatedBands: Band[];
}

export interface PollBandChangesResult {
  success: boolean;
  bands: Band[];
  changes?: BandChanges;
  timeout?: boolean;
}

export interface UsePollBandChangesOptions {
  initialDelay?: number;
  pollInterval?: number;
  maxDuration?: number;
  onStatusChange?: (status: string) => void;
  onSuccess?: (result: PollBandChangesResult) => void;
}

export type PollForBandChangesFunction = (
  pgaId: string,
  expectedChanges?: BandChangeExpectation,
  baselineBands?: Band[]
) => Promise<PollBandChangesResult>;

const DEFAULT_INITIAL_DELAY = 1500; // 1.5 seconds
const DEFAULT_POLL_INTERVAL = 2500; // 2.5 seconds
const DEFAULT_MAX_DURATION = 45000; // 45 seconds

/**
 * Filters out bands without a classification
 */
function filterBandsWithClassification(bands: Band[]): Band[] {
  return bands.filter((band) => band.classification?.name);
}

/**
 * Normalizes bands for comparison by sorting and filtering
 */
function normalizeBands(bands: Band[]): Band[] {
  return filterBandsWithClassification(bands).sort((a, b) => {
    const dateA = new Date(a.startedOn).getTime();
    const dateB = new Date(b.startedOn).getTime();
    return dateB - dateA; // Most recent first
  });
}

/**
 * Detects changes between baseline and current bands
 */
function detectBandChanges(baseline: Band[], current: Band[], expectedChanges?: BandChangeExpectation): BandChanges {
  const baselineNormalized = normalizeBands(baseline);
  const currentNormalized = normalizeBands(current);

  const newBands: Band[] = [];
  const updatedBands: Band[] = [];
  const terminatedBands: Band[] = [];

  // Find new bands (not in baseline)
  for (const currentBand of currentNormalized) {
    const existsInBaseline = baselineNormalized.some((b) => b.id === currentBand.id);
    if (!existsInBaseline) {
      newBands.push(currentBand);
    }
  }

  // Find updated or terminated bands
  for (const currentBand of currentNormalized) {
    const baselineBand = baselineNormalized.find((b) => b.id === currentBand.id);
    if (baselineBand) {
      // Check for updates - comprehensive field comparison
      const classificationChanged =
        baselineBand.classification?.name !== currentBand.classification?.name ||
        baselineBand.classification?.id !== currentBand.classification?.id;
      const sectionChanged = baselineBand.section?.legacyId !== currentBand.section?.legacyId;
      const expiresOnChanged = baselineBand.expiresOn !== currentBand.expiresOn;
      const terminatesOnChanged = baselineBand.terminatesOn !== currentBand.terminatesOn;
      const startedOnChanged = baselineBand.startedOn !== currentBand.startedOn;
      const createdReasonChanged = baselineBand.createdReason !== currentBand.createdReason;
      const terminatedReasonChanged = baselineBand.terminatedReason !== currentBand.terminatedReason;

      // Check sectionAffiliation changes (including primary flag)
      const sectionAffiliationChanged =
        baselineBand.sectionAffiliation?.id !== currentBand.sectionAffiliation?.id ||
        baselineBand.sectionAffiliation?.primary !== currentBand.sectionAffiliation?.primary ||
        baselineBand.sectionAffiliation?.section?.id !== currentBand.sectionAffiliation?.section?.id;

      // Check programType changes
      const programTypeChanged = baselineBand.programType?.id !== currentBand.programType?.id;

      if (
        classificationChanged ||
        sectionChanged ||
        expiresOnChanged ||
        terminatesOnChanged ||
        startedOnChanged ||
        createdReasonChanged ||
        terminatedReasonChanged ||
        sectionAffiliationChanged ||
        programTypeChanged
      ) {
        if (currentBand.terminatesOn && !baselineBand.terminatesOn) {
          terminatedBands.push(currentBand);
        } else {
          updatedBands.push(currentBand);
        }
      }
    }
  }

  // Check if expected changes are present
  let expectedChangesDetected = true;
  if (expectedChanges) {
    expectedChangesDetected = false;

    // If baseline is empty and we have new bands, consider it detected (bands were created)
    if (baselineNormalized.length === 0 && newBands.length > 0) {
      expectedChangesDetected = true;
    } else {
      // Check for new band matching expected startedOn
      if (expectedChanges.startedOn) {
        const matchingNewBand = newBands.find((b) => b.startedOn === expectedChanges.startedOn);
        if (matchingNewBand) {
          expectedChangesDetected = true;
        }
      }

      // Check for section change
      if (expectedChanges.sectionLegacyId) {
        const matchingBand = currentNormalized.find((b) => b.section?.legacyId === expectedChanges.sectionLegacyId);
        if (matchingBand) {
          expectedChangesDetected = true;
        }
      }

      // Check for classification change
      if (expectedChanges.classificationName) {
        const matchingBand = currentNormalized.find(
          (b) => b.classification?.name === expectedChanges.classificationName
        );
        if (matchingBand) {
          expectedChangesDetected = true;
        }
      }
    }
  }

  const detected = newBands.length > 0 || updatedBands.length > 0 || terminatedBands.length > 0;

  return {
    detected: detected && (expectedChanges ? expectedChangesDetected : true),
    newBands,
    updatedBands,
    terminatedBands
  };
}

/**
 * Custom hook for polling person bands to detect changes after employment mutations
 */
export function usePollBandChanges(options: UsePollBandChangesOptions = {}) {
  const {
    initialDelay = DEFAULT_INITIAL_DELAY,
    pollInterval = DEFAULT_POLL_INTERVAL,
    maxDuration = DEFAULT_MAX_DURATION,
    onStatusChange,
    onSuccess
  } = options;

  const [fetchPersonBands] = useLazyQuery<{ person: { id: string; bands: Band[] } }, { pgaId: string }>(
    PERSON_BANDS_QUERY,
    {
      // Note: fetchPolicy is set per-query call, not here
      errorPolicy: "all" // Return both data and errors so we can log them
    }
  );

  const [isPolling, setIsPolling] = useState(false);
  const [status, setStatus] = useState<string>("");
  const abortControllerRef = useRef<AbortController | null>(null);
  const pollingStartTimeRef = useRef<number | null>(null);

  const updateStatus = useCallback(
    (newStatus: string) => {
      setStatus(newStatus);
      onStatusChange?.(newStatus);
    },
    [onStatusChange]
  );

  const pollForBandChanges = useCallback(
    async (
      pgaId: string,
      expectedChanges?: BandChangeExpectation,
      providedBaseline?: Band[]
    ): Promise<PollBandChangesResult> => {
      // Cancel any existing polling
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      pollingStartTimeRef.current = Date.now();

      setIsPolling(true);

      try {
        let baselineBands: Band[];

        // Use provided baseline if available, otherwise capture it now
        if (providedBaseline) {
          baselineBands = providedBaseline;
        } else {
          updateStatus("Capturing baseline bands...");
          const baselineResult = await fetchPersonBands({
            variables: { pgaId },
            // Force a fresh network request, bypass cache
            fetchPolicy: "network-only"
          });

          if (baselineResult.error) {
            console.error("[BandPolling] GraphQL error in baseline query:", baselineResult.error);
          }

          if (abortController.signal.aborted) {
            return { success: false, bands: [] };
          }

          baselineBands = baselineResult.data?.person?.bands || [];
        }

        updateStatus("Polling for band changes...");

        // Initial delay
        await new Promise((resolve) => setTimeout(resolve, initialDelay));

        if (abortController.signal.aborted) {
          return { success: false, bands: [] };
        }

        const startTime = Date.now();
        let pollAttempt = 0;

        // Polling loop
        while (Date.now() - startTime < maxDuration) {
          if (abortController.signal.aborted) {
            return { success: false, bands: [] };
          }

          pollAttempt++;
          const currentResult = await fetchPersonBands({
            variables: { pgaId },
            // Force a fresh network request, bypass cache
            fetchPolicy: "network-only"
          });

          if (currentResult.error) {
            console.error("[BandPolling] GraphQL error during polling:", currentResult.error);
          }

          if (abortController.signal.aborted) {
            return { success: false, bands: [] };
          }

          const currentBands = currentResult.data?.person?.bands || [];
          const changes = detectBandChanges(baselineBands, currentBands, expectedChanges);

          if (changes.detected) {
            const result = {
              success: true,
              bands: currentBands,
              changes
            };

            updateStatus("Band changes detected!");

            // Call success callback if provided
            if (onSuccess) {
              onSuccess(result);
            }

            return result;
          }

          // Wait before next poll
          await new Promise((resolve) => setTimeout(resolve, pollInterval));
        }

        // Timeout reached
        updateStatus("Band changes may still be processing. Please refresh in a moment.");
        return {
          success: false,
          bands: baselineBands,
          timeout: true
        };
      } catch (error) {
        console.error("[BandPolling] Error polling for band changes:", error);
        updateStatus("Error checking for band changes.");
        return {
          success: false,
          bands: []
        };
      } finally {
        setIsPolling(false);
        pollingStartTimeRef.current = null;
        if (!abortController.signal.aborted) {
          updateStatus("");
        }
      }
    },
    [fetchPersonBands, initialDelay, pollInterval, maxDuration, updateStatus, onSuccess]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Helper function to fetch current bands (for capturing baseline before mutation)
  const fetchCurrentBands = useCallback(
    async (pgaId: string): Promise<Band[]> => {
      try {
        const result = await fetchPersonBands({
          variables: { pgaId },
          fetchPolicy: "network-only"
        });
        return result.data?.person?.bands || [];
      } catch (error) {
        console.error("[BandPolling] Error fetching current bands:", error);
        return [];
      }
    },
    [fetchPersonBands]
  );

  return {
    pollForBandChanges,
    fetchCurrentBands,
    isPolling,
    status
  };
}
